/**
 * geometry.js — Carousel Composer slide geometry, crops and panorama math.
 *
 * Ported from the standalone Carousel Composer artifact with the formulas,
 * clamps and rounding unchanged. The preview positions its cells from
 * slideCells() and the export draws into the same rectangles, so what you
 * frame is exactly what saves, whatever the layout.
 *
 * State is plain data and every helper returns new objects:
 *   slides: [{ id, layout, cells: [{ imgId, zoom, ox, oy } | null], split?, pano?: { gid, i } }]
 *   panos:  { [gid]: { imgId, n, start, oy } }
 *   design: { fmt, gap, pad, bg, preset }
 *
 * A panorama slide's cells[0] is never authoritative. Its crop is derived from
 * the group on every render and export (effectivePanoCell), which is what keeps
 * the seams intact across a format, border or gap change. Anything that takes a
 * slide out of its group materializes that derived crop first.
 */

import { cellLook, viewPhoto } from './look.js';
import { getLayout, isSpreadLayout, layoutCellCounts } from './layouts.js';

export const FORMATS = { '4:5': [1080, 1350], '1:1': [1080, 1080], '1.91:1': [1080, 566] };
// The quick styles, plus every registry layout: persist.js treats a layout it
// cannot find here as 'single', so a new layout has to be countable to survive a reload.
export const CELLS = { single: 1, stack: 2, row: 2, hero3: 4, ...layoutCellCounts() };
// The quick styles only: this is what the slide panel's switcher offers. Everything
// else is named by the registry and reached from the layout gallery.
export const LAYOUT_NAMES = { single: 'Single', stack: 'Stacked', row: 'Side by side', hero3: 'Hero + 3' };

// Carousel styles: gap, border and fill as one choice.
export const PRESETS = {
  seamless: { gap: 0, pad: 0, bg: '#ffffff' },
  hairline: { gap: 6, pad: 0, bg: '#ffffff' },
  open: { gap: 16, pad: 0, bg: '#ffffff' },
  frame: { gap: 18, pad: 32, bg: '#ffffff' },
  mat: { gap: 18, pad: 32, bg: '#0E1113' },
  wide: { gap: 26, pad: 64, bg: '#F2EFE9' },
};

// Seamless by default: most carousels read as one run of photography, and a gap
// is a deliberate choice rather than a starting point.
export const DEFAULT_DESIGN = { fmt: '4:5', gap: 0, pad: 0, bg: '#ffffff', preset: 'seamless' };
export const DEFAULT_EXPORT_OPTS = { qual: 1440, type: 'jpg', prefix: 'shoreline', bundle: 'files' };

export function formatSize(fmt) {
  return FORMATS[fmt] || FORMATS['4:5'];
}

export function cellCount(layout) {
  return CELLS[layout] || 1;
}

export function newCell(imgId) {
  return { imgId, zoom: 1, ox: 0.5, oy: 0.5 };
}

// Share of the height (net of the middle gap) given to the hero photo.
export function heroSplit(sl) {
  const v = sl && typeof sl.split === 'number' ? sl.split : 0.42;
  return Math.min(0.7, Math.max(0.25, v));
}

// Visible source rectangle, in image pixels, for a cell of aspect cw/ch.
export function srcRect(im, cw, ch, cell) {
  const ca = cw / ch, ia = im.w / im.h, z = cell.zoom;
  let sw, sh;
  if (ia > ca) { sh = im.h / z; sw = sh * ca; }
  else { sw = im.w / z; sh = sw / ca; }
  return { sx: cell.ox * (im.w - sw), sy: cell.oy * (im.h - sh), sw, sh };
}

/**
 * How a photo sits in its cell. 'fill' crops it to cover the cell (the default,
 * and the only thing the artifact did); 'contain' shows all of it, letterboxed on
 * the slide's fill colour. Panorama parts are always 'fill' — a seam has to meet.
 */
export function cellFit(sl, cell) {
  if (sl && sl.pano) return 'fill';
  return cell && cell.fit === 'contain' ? 'contain' : 'fill';
}

/**
 * The look a cell paints with — the one rule the board, the slide card and the
 * export all read, so a photo cannot grade one way on screen and another in the
 * file. A panorama part's cells[0] is derived from the group on every render, so
 * an opt-out stored there is not the run's to honour: a pano cell always shows
 * the photo's own look.
 */
export function cellLookAt(sl, im, ci) {
  return cellLook(im, sl && sl.pano ? null : sl && sl.cells[ci]);
}

/**
 * Where the photo lands in a cell of cw x ch.
 *   fill    -> { mode:'fill', src }  the visible source rectangle, as ever
 *   contain -> { mode:'contain', dx, dy, dw, dh }  the destination box the whole
 *              photo is drawn into; it overflows the cell once zoomed past fitting,
 *              and ox/oy place it in the free space exactly as CSS percentages do.
 */
export function cellPlacement(im, cw, ch, cell, fit) {
  if (fit === 'contain') {
    const scale = Math.min(cw / im.w, ch / im.h) * (cell.zoom || 1);
    const dw = im.w * scale, dh = im.h * scale;
    return { mode: 'contain', dx: (cw - dw) * cell.ox, dy: (ch - dh) * cell.oy, dw, dh };
  }
  return { mode: 'fill', src: srcRect(im, cw, ch, cell) };
}

// Panorama parts ignore the border and gap so the seams don't show. A SPREAD is a
// panorama run that keeps them: its layout carries the margin across the seam itself.
export function slideGeom(sl, design) {
  if (sl && sl.pano && !isSpreadLayout(sl.layout)) return { pad: 0, gap: 0 };
  return { pad: design.pad, gap: design.gap };
}

// A registry layout tunes its margins as fractions, so a heavy border can drive a
// rectangle off the slide. Nothing may be drawn outside it, so every rect lands back
// inside here rather than in nine separate layouts.
function clampRects(rects, W, H) {
  return rects.map((r) => {
    const x = Math.min(Math.max(0, r.x), W - 1), y = Math.min(Math.max(0, r.y), H - 1);
    const out = { x, y, w: Math.max(1, Math.min(r.w, W - x)), h: Math.max(1, Math.min(r.h, H - y)) };
    if (r.mat) out.mat = Math.max(0, r.mat);
    return out;
  });
}

/**
 * Every cell of a slide as {x,y,w,h} in slide pixels, in DRAW ORDER: a later
 * rectangle may overlap an earlier one, and the preview (DOM order) and the
 * export (draw order) both honour that.
 *
 * A registry layout may also return `mat` on a rectangle — the px of the design's
 * fill colour to paint around it before its photo, so an overlapping frame reads
 * as a print on a print. The rectangle stays authoritative either way: crop,
 * drag and export all use it whether or not the ring is painted.
 */
export function slideCells(sl, W, H, pad, gap) {
  const iw = Math.max(2, W - 2 * pad), ih = Math.max(2, H - 2 * pad);
  const L = sl ? sl.layout : 'single';
  const reg = getLayout(L);
  if (reg) return clampRects(reg.rects(W, H, { pad, gap }, sl), W, H);
  if (L === 'stack') {
    const h = Math.max(1, (ih - gap) / 2);
    return [{ x: pad, y: pad, w: iw, h }, { x: pad, y: pad + h + gap, w: iw, h }];
  }
  if (L === 'row') {
    const w = Math.max(1, (iw - gap) / 2);
    return [{ x: pad, y: pad, w, h: ih }, { x: pad + w + gap, y: pad, w, h: ih }];
  }
  if (L === 'hero3') {
    const topH = Math.max(1, (ih - gap) * heroSplit(sl));
    const botH = Math.max(1, ih - gap - topH);
    const colW = Math.max(1, (iw - gap) / 2);
    const rowH = Math.max(1, (botH - gap) / 2);
    const y2 = pad + topH + gap, x2 = pad + colW + gap;
    return [
      { x: pad, y: pad, w: iw, h: topH },            // hero, full width
      { x: pad, y: y2, w: colW, h: botH },           // tall, bottom left
      { x: x2, y: y2, w: colW, h: rowH },            // right, upper
      { x: x2, y: y2 + rowH + gap, w: colW, h: rowH }, // right, lower
    ];
  }
  return [{ x: pad, y: pad, w: iw, h: ih }];
}

export function cellRoleShort(sl, ci) {
  const reg = getLayout(sl.layout);
  if (reg) return (reg.roles && reg.roles[ci]) || '';
  if (sl.layout === 'stack') return ['top', 'bottom'][ci] || '';
  if (sl.layout === 'row') return ['left', 'right'][ci] || '';
  if (sl.layout === 'hero3') return ['top', 'tall left', 'right top', 'right btm'][ci] || '';
  return '';
}

export function cellRoleName(sl, ci) {
  const reg = getLayout(sl.layout);
  if (reg) return (reg.roleNames && reg.roleNames[ci]) || 'photo';
  if (sl.pano) return 'panorama photo';
  if (sl.layout === 'stack') return ci ? 'bottom photo' : 'top photo';
  if (sl.layout === 'row') return ci ? 'right photo' : 'left photo';
  if (sl.layout === 'hero3') return ['top photo', 'tall left photo', 'upper right photo', 'lower right photo'][ci] || 'photo';
  return 'photo';
}

export function layoutName(sl, panos) {
  const reg = getLayout(sl.layout);
  if (sl.pano) {
    const g = panos && panos[sl.pano.gid];
    return (reg ? reg.name : 'Panorama') + ' ' + (sl.pano.i + 1) + ' of ' + (g ? g.n : '?');
  }
  return LAYOUT_NAMES[sl.layout] || (reg ? reg.name : '') || 'Slide';
}

/* ---------------- panorama ---------------- */

// Strip geometry for the current cell aspect. sw/sh are in image pixels;
// n strips of width sw sit side by side, so swiping pans across the photo.
export function panoStrip(g, im, cw, ch) {
  const ca = cw / ch;
  const sw = Math.min(im.w / g.n, im.h * ca);
  const sh = sw / ca;
  return { sw, sh, slack: im.w - g.n * sw, ca };
}

// The artifact's syncPano without the mutation: the crop a pano part shows for a
// cell of cw x ch. Falls back to cells[0] as stored when the group or its photo
// is missing, exactly where syncPano would have left the cell untouched.
export function effectivePanoCell(sl, panos, imgById, cw, ch) {
  const cell = sl.cells[0] || null;
  const g = sl.pano && panos ? panos[sl.pano.gid] : null;
  if (!g) return cell;
  const im = imgById(g.imgId);
  if (!im || !cell) return cell;
  const st = panoStrip(g, im, cw, ch);
  const sx = g.start * st.slack + sl.pano.i * st.sw;
  return {
    ...cell,
    imgId: g.imgId,
    zoom: (im.w / im.h > st.ca) ? (im.h / st.sh) : (im.w / st.sw),
    ox: (im.w - st.sw) > 0 ? Math.min(1, Math.max(0, sx / (im.w - st.sw))) : 0.5,
    oy: g.oy,
  };
}

// The photo a cell shows, without needing cell dimensions. For a pano part this is
// the group's photo (pass imgById to fall back to cells[0] when that photo is gone).
export function cellImgId(sl, ci, panos, imgById) {
  const c = sl && sl.cells[ci];
  if (!c) return null;
  const g = ci === 0 && sl.pano && panos ? panos[sl.pano.gid] : null;
  if (g && (!imgById || imgById(g.imgId))) return g.imgId;
  return c.imgId;
}

// The crop a cell shows on a W x H slide (defaults to the design's format size).
export function effectiveCell(sl, ci, panos, imgById, W, H, design) {
  if (!sl.pano || ci !== 0) return sl.cells[ci] || null;
  const [W0, H0] = formatSize(design.fmt);
  const g0 = slideGeom(sl, design);
  const rc = slideCells(sl, W == null ? W0 : W, H == null ? H0 : H, g0.pad, g0.gap)[0];
  return effectivePanoCell(sl, panos, imgById, rc.w, rc.h);
}

// cells with a pano part's derived crop written into cells[0], at the design format size.
export function materializeCells(sl, ctx) {
  if (!sl.pano) return sl.cells;
  const cells = sl.cells.slice();
  cells[0] = effectiveCell(sl, 0, ctx.panos, ctx.imgById, null, null, ctx.design);
  return cells;
}

// layout defaults to 'single' — full bleed, as every panorama was before spreads.
export function makePano(imgId, n, uid, layout = 'single') {
  const gid = uid();
  const group = { imgId, n: Math.min(6, Math.max(2, n | 0)), start: 0.5, oy: 0.5 };
  const slides = [];
  const parts = cellCount(layout);
  for (let i = 0; i < group.n; i++) {
    const cells = [newCell(imgId)];
    while (cells.length < parts) cells.push(null);
    slides.push({ id: uid(), layout, cells, pano: { gid, i } });
  }
  return { gid, group, slides };
}

/* ---------------- slide edits (immutable) ---------------- */

export function makeSlide(layout, ids, uid) {
  const n = cellCount(layout);
  const cells = [];
  for (let i = 0; i < n; i++) cells.push(ids[i] ? newCell(ids[i]) : null);
  return { id: uid(), layout, cells };
}

// Chunks ids by the layout's cell count: pairs by two, hero grids by four.
export function chunkSlides(layout, ids, uid) {
  const n = cellCount(layout);
  const out = [];
  for (let i = 0; i < ids.length; i += n) out.push(makeSlide(layout, ids.slice(i, i + n), uid));
  return out;
}

// ctx: { panos, imgById, design }
export function setLayout(sl, layout, ctx) {
  const n = cellCount(layout);
  const dropPano = !!sl.pano && layout !== 'single' && !isSpreadLayout(layout);
  const cells = (dropPano ? materializeCells(sl, ctx) : sl.cells).slice(0, n);
  while (cells.length < n) cells.push(null);
  const next = { ...sl, layout, cells };
  if (dropPano) delete next.pano;
  return next;
}

// Copies layout, cells and split only, so a duplicated pano part is a detached single.
export function duplicateSlide(sl, ctx, uid) {
  return { id: uid(), layout: sl.layout, cells: JSON.parse(JSON.stringify(materializeCells(sl, ctx))), split: sl.split };
}

export function detachPano(sl, ctx) {
  const next = { ...sl, cells: materializeCells(sl, ctx) };
  delete next.pano;
  return next;
}

// Every part of a panorama shows the same photo, so pointing one part at a new
// frame repoints the whole run.
export function assignPhoto(slides, panos, si, ci, imgId) {
  const sl = slides[si];
  if (!sl) return { slides, panos };
  // cells[0] is the run's; a spread's other cells are this slide's alone
  const g = ci === 0 && sl.pano && panos ? panos[sl.pano.gid] : null;
  if (g) return { slides, panos: { ...panos, [sl.pano.gid]: { ...g, imgId } } };
  const cells = sl.cells.slice();
  cells[ci] = newCell(imgId);
  const next = slides.slice();
  next[si] = { ...sl, cells };
  return { slides: next, panos };
}

/**
 * The slides grouped into the blocks that move as one: a panorama run is a single
 * block, everything else is a block of one. Reordering works on blocks, so a run
 * can never be split or landed inside.
 *   [{ from, to, gid }]  — inclusive index range in `slides`
 */
export function slideUnits(slides) {
  const units = [];
  for (let i = 0; i < slides.length; i++) {
    const gid = slides[i].pano ? slides[i].pano.gid : null;
    if (!gid) { units.push({ from: i, to: i, gid: null }); continue; }
    let j = i;
    while (j + 1 < slides.length && slides[j + 1].pano && slides[j + 1].pano.gid === gid) j++;
    units.push({ from: i, to: j, gid });
    i = j;
  }
  return units;
}

/**
 * Moves the block containing `si` so that it starts at `target` (an index in the
 * current list). Landing inside another block snaps past it, so a panorama stays
 * whole at both ends of the move.
 */
export function moveSlideTo(slides, si, target) {
  const units = slideUnits(slides);
  const from = units.findIndex((u) => si >= u.from && si <= u.to);
  if (from < 0) return slides;
  // which block currently owns the target position
  let to = units.findIndex((u) => target >= u.from && target <= u.to);
  if (to < 0) to = units.length - 1;
  // dropping into the back half of a block means "after it"
  if (to !== from && target > units[to].from && units[to].gid) to += 1;
  if (to === from) return slides;
  const order = units.slice();
  const [moved] = order.splice(from, 1);
  order.splice(Math.min(order.length, to > from ? to - 1 : to), 0, moved);
  const out = [];
  order.forEach((u) => { for (let i = u.from; i <= u.to; i++) out.push(slides[i]); });
  return out;
}

export function moveSlide(slides, si, d) {
  const j = si + d;
  if (j < 0 || j >= slides.length) return slides;
  const next = slides.slice();
  const [s] = next.splice(si, 1);
  next.splice(j, 0, s);
  return next;
}

// Leaves the group (and its n) untouched, as the artifact does.
export function deleteSlide(slides, si) {
  return slides.filter((_, i) => i !== si);
}

// Hero grids and registry layouts rotate positions; pairs swap. A spread part never
// moves its cells: cells[0] belongs to the run.
export function swapCells(sl) {
  if (sl.pano) return sl;
  const c = sl.cells.slice();
  if (sl.layout === 'hero3' || getLayout(sl.layout)) c.unshift(c.pop());
  else c.reverse();
  return { ...sl, cells: c };
}

export function updateCell(sl, ci, patch) {
  if (!sl.cells[ci]) return sl;
  const cells = sl.cells.slice();
  cells[ci] = { ...cells[ci], ...patch };
  return { ...sl, cells };
}

// Double-click / reset: a pano recentres its group, anything else resets the cell.
export function resetCrop(slides, panos, si, ci) {
  const sl = slides[si];
  if (!sl) return { slides, panos };
  const g = ci === 0 && sl.pano && panos ? panos[sl.pano.gid] : null;
  if (g) return { slides, panos: { ...panos, [sl.pano.gid]: { ...g, start: 0.5, oy: 0.5 } } };
  if (!sl.cells[ci]) return { slides, panos };
  const next = slides.slice();
  next[si] = updateCell(sl, ci, { zoom: 1, ox: 0.5, oy: 0.5 });
  return { slides: next, panos };
}

export function wheelZoom(zoom, deltaY) {
  return Math.min(6, Math.max(1, zoom * (deltaY < 0 ? 1.06 : 1 / 1.06)));
}

export function zoomSliderMax(zoom) {
  return Math.max(3, Math.ceil(zoom));
}

/* ---------------- drag to reframe ---------------- */

// Snapshot at pointerdown. displayWidth is the cell's on-screen width in CSS px.
// Returns null when the cell has no photo. ctx: { panos, imgById, design }
export function beginPan(sl, ci, ctx, displayWidth) {
  const [W, H] = formatSize(ctx.design.fmt);
  const g0 = slideGeom(sl, ctx.design);
  const rects = slideCells(sl, W, H, g0.pad, g0.gap);
  const rc = rects[ci] || rects[0];
  const cw = rc.w, ch = rc.h;
  const cell = sl.pano && ci === 0 ? effectivePanoCell(sl, ctx.panos, ctx.imgById, cw, ch) : sl.cells[ci];
  const im0 = cell ? ctx.imgById(cell.imgId) : null;
  if (!im0) return null;
  const im = viewPhoto(im0, cellLook(im0, sl.cells[ci]));
  if (cellFit(sl, cell) === 'contain') {
    // Letterboxed: drag moves the photo itself, so the offsets follow the pointer.
    const pl = cellPlacement(im, cw, ch, cell, 'contain');
    const perCell = displayWidth / cw;
    return {
      ci,
      gid: null,
      contain: true,
      slackDisp: (cw - pl.dw) * perCell,
      ovYdisp: (ch - pl.dh) * perCell,
      x0: cell.ox,
      y0: cell.oy,
    };
  }
  const r = srcRect(im, cw, ch, cell);
  const scale = displayWidth / r.sw;
  const g = sl.pano && ctx.panos ? ctx.panos[sl.pano.gid] : null;
  const st = g ? panoStrip(g, im, cw, ch) : null;
  return {
    ci,
    gid: g ? sl.pano.gid : null,
    slackDisp: g ? st.slack * scale : (im.w - r.sw) * scale,
    ovYdisp: g ? (im.h - st.sh) * scale : (im.h - r.sh) * scale,
    x0: g ? g.start : cell.ox,
    y0: g ? g.oy : cell.oy,
  };
}

// dx/dy: pointer travel in CSS px since pointerdown.
export function panTo(pan, dx, dy) {
  if (pan.contain) {
    const px = Math.abs(pan.slackDisp) > 0.5 ? Math.min(1, Math.max(0, pan.x0 + dx / pan.slackDisp)) : pan.x0;
    const py = Math.abs(pan.ovYdisp) > 0.5 ? Math.min(1, Math.max(0, pan.y0 + dy / pan.ovYdisp)) : pan.y0;
    return { x: px, y: py };
  }
  const x = pan.slackDisp > 0.5 ? Math.min(1, Math.max(0, pan.x0 - dx / pan.slackDisp)) : pan.x0;
  const y = pan.ovYdisp > 0.5 ? Math.min(1, Math.max(0, pan.y0 - dy / pan.ovYdisp)) : pan.y0;
  return { x, y };
}

// Writes a panTo() result back: a pano moves its group's start/oy, a cell its ox/oy.
export function applyPan(slides, panos, si, pan, pos) {
  if (pan.gid) {
    const g = panos[pan.gid];
    if (!g) return { slides, panos };
    return { slides, panos: { ...panos, [pan.gid]: { ...g, start: pos.x, oy: pos.y } } };
  }
  const sl = slides[si];
  if (!sl || !sl.cells[pan.ci]) return { slides, panos };
  const next = slides.slice();
  next[si] = updateCell(sl, pan.ci, { ox: pos.x, oy: pos.y });
  return { slides: next, panos };
}

/* ---------------- design ---------------- */

// 'custom' (or any unknown key) only records the choice, as the artifact's select did.
export function applyPreset(design, key) {
  const pr = PRESETS[key];
  if (!pr) return { ...design, preset: key };
  return { ...design, preset: key, gap: pr.gap, pad: pr.pad, bg: pr.bg };
}

// Gap, border and fill edits clamp like the inputs did and flip the style to Custom.
export function updateDesign(design, patch) {
  const next = { ...design };
  let custom = false;
  if ('fmt' in patch) next.fmt = patch.fmt;
  if ('gap' in patch) { next.gap = Math.min(200, Math.max(0, +patch.gap || 0)); custom = true; }
  if ('pad' in patch) { next.pad = Math.min(240, Math.max(0, +patch.pad || 0)); custom = true; }
  if ('bg' in patch) { next.bg = patch.bg; custom = true; }
  if (custom) next.preset = 'custom';
  return next;
}
